import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, firstValueFrom, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AbsenceModalComponent } from 'src/app/absences/absence-form/absence-form.component';
import { AbsencesRefreshService } from 'src/app/absences/services/absences-refresh.service';
import { Messages } from 'src/app/common/enums';
import { AbsenceService } from 'src/app/data/absence.service';
import { WorkerAgreementService } from 'src/app/data/worker-agreement.service';
import { AbsenceDto } from 'src/app/models/dtos/absence-dto';
import { AgreementToPick } from 'src/app/models/dtos/agreement-to-pick-dto';
import { WorkingTimePeriodDto, WorkingTimePeriodsDto } from 'src/app/models/dtos/absences/working-time-periods-dto';
import { findPeriod, isPartTimePeriod, toDateKey } from 'src/app/absences/services/working-time-periods';
import { EmploymentType } from 'src/app/models/enums/employment-type-enum';
import { WorkingTimeEnum } from 'src/app/models/enums/working-time-enum';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

export enum AbsenceTabsType {
  Absences = 'Absences',
  AbsenceLimits = 'AbsenceLimits',
  Illness = 'SickLeaves',
}

@Component({
  selector: 'app-absences',
  templateUrl: './absences.component.html',
  styleUrls: ['./absences.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AbsencesRefreshService],
})
export class AbsencesComponent {
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private workerAgreementService = inject(WorkerAgreementService);
  private router = inject(Router);
  private location = inject(Location);
  private absenceService = inject(AbsenceService);
  private snackbar = inject(SnackBarService);
  private refreshService = inject(AbsencesRefreshService);
  readonly WorkingTimeEnum = WorkingTimeEnum;
  readonly EmploymentType = EmploymentType;
  readonly isRefreshRequestInProgress = signal(false);

  readonly agreements = signal<AgreementToPick[]>([]);
  readonly agreements$ = toObservable(this.agreements);
  readonly workingTimePeriods = signal<WorkingTimePeriodsDto | null>(null);
  readonly selectedPeriod = signal<WorkingTimePeriodDto | null>(null);
  readonly workingTimePeriodsLoading = signal(false);
  readonly workingTimePeriodsError = signal(false);
  readonly isScaled = computed(() => !!this.workingTimePeriods()?.IsScaled);
  readonly canCreateAbsence = computed(() => !!this.workerAgreement
    && !this.workingTimePeriodsLoading() && !this.workingTimePeriodsError()
    && this.workingTimePeriods() != null && (!this.isScaled() || this.selectedPeriod() != null));
  readonly displayedWorkingTime = computed(() => this.isScaled()
    ? this.selectedPeriod()?.WorkingTime ?? '—' : this.workerAgreement?.WorkingTime ?? '—');
  readonly showPartTimeInfo = computed(() => {
    const agreement = this.workerAgreement;
    if (!agreement || (agreement.EmploymentTypeId !== EmploymentType.EmploymentAgreement
      && agreement.EmploymentTypeId !== EmploymentType.TemporaryEmploymentAgreement)) {
      return false;
    }
    if (this.isScaled()) {
      return isPartTimePeriod(this.selectedPeriod());
    }
    return agreement.WorkingTimeId !== WorkingTimeEnum.FullTime;
  });
  private workingTimePeriodsSubscription: Subscription | null = null;
  private workingTimePeriodsAgreementId: number | null = null;

  private loadAgreements(): void {
    const workerId = this.workerId();

    if (!workerId) {
      this.agreements.set([]);
      this.workerAgreement = null;
      this.loadWorkingTimePeriods(null);
      return;
    }

    this.workerAgreementService
      .getAgreementsByWorkerId(+workerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((agreements) => {
        const nextAgreements = agreements ?? [];
        this.agreements.set(nextAgreements);

        if (this.workerAgreement) {
          const selectedAgreement = nextAgreements.find((agreement) => agreement.Id === this.workerAgreement!.Id) ?? null;
          this.workerAgreement = selectedAgreement;
          this.loadWorkingTimePeriods(selectedAgreement);
        }
      });
  }

  private triggerReload(): void {
    this.loadWorkingTimePeriods(this.workerAgreement, true);
    this.refreshService.refresh();
    this.loadAgreements();
  }

  readonly workerId = input<string | undefined>(undefined);
  readonly absenceId = input<string | undefined>(undefined);
  readonly tabType = input<AbsenceTabsType | undefined>(undefined);
  readonly year = input<string | undefined>(undefined);

  readonly tabGroup = viewChild(MatTabGroup);

  navigation = [
    {
      listType: AbsenceTabsType.AbsenceLimits,
      guardPredicate: () => this.workerAgreement?.HasAbsences,
      link: 'limits',
    },
    {
      listType: AbsenceTabsType.Absences,
      guardPredicate: () => this.workerAgreement?.HasAbsences,
      link: '../absences',
    },
    {
      listType: AbsenceTabsType.Illness,
      guardPredicate: () => this.workerAgreement?.HasSickLeaves,
      link: 'illness',
    },
  ];
  
  private readonly selectedAgreement = signal<AgreementToPick | null>(null);
  get workerAgreement(): AgreementToPick | null { return this.selectedAgreement(); }
  set workerAgreement(value: AgreementToPick | null) { this.selectedAgreement.set(value); }
  selectedYear = new Date().getFullYear();

  private readonly destroyRef = inject(DestroyRef);
  private localTabType: AbsenceTabsType | undefined;
  private selectedNavigation: (typeof this.navigation)[0] | undefined;

  private lastOpenedAbsenceId: number | null = null;
  private lastLoadedWorkerId: string | undefined;
  private lastTabType: AbsenceTabsType | undefined;

  constructor() {
    effect(() => {
      const tabType = this.tabType();
      const year = this.year();
      const workerId = this.workerId();
      const absenceId = this.absenceId();

      if (tabType) {
        this.localTabType = tabType;

        if (tabType !== this.lastTabType) {
          this.lastTabType = tabType;
          this.setSelectedTabFromUrl();
        }
      }

      const selectedYear = this.tryParseYear(year);
      if (selectedYear != null) {
        this.selectedYear = selectedYear;
      }

      if (!workerId) {
        this.lastLoadedWorkerId = undefined;
        this.agreements.set([]);
        this.workerAgreement = null;
        this.loadWorkingTimePeriods(null);
        return;
      }

      if (workerId !== this.lastLoadedWorkerId) {
        this.lastLoadedWorkerId = workerId;
        this.loadAgreements();
      }

      if (absenceId) {
        if (Number(absenceId) !== this.lastOpenedAbsenceId) {
          this.lastOpenedAbsenceId = Number(absenceId);
          this.openModal(Number(absenceId));
        }
      } else {
        this.lastOpenedAbsenceId = null;
      }
    });
  }

  ngOnInit(): void {
    this.setSelectedTabFromUrl();
  }

  onYearChange = (year: number): void => {
    if (this.selectedYear === year) {
      return;
    }

    this.selectedYear = year;
    this.setUrl();
  };

  onAgreementSelection = (agreement: AgreementToPick | null) => {
    this.workerAgreement = agreement;
    this.loadWorkingTimePeriods(agreement);
    this.setSelectedTabFromUrl();
  };

  openAbsenceModal = async (absence: AbsenceDto | null = null) => {
    if (!absence && !this.canCreateAbsence()) {
      return;
    }
    const dialogRef = this.dialog.open(AbsenceModalComponent, {
      maxHeight: '90vh',
      data: this.absenceModalData(absence),
    });

    const isModified = await firstValueFrom(dialogRef.afterClosed());

    if (isModified) {
      this.triggerReload();
    }
  };

  private absenceModalData = (absence: AbsenceDto | null = null) => ({
    WorkerId: +this.workerId()!,
    Absence: absence,
    WorkerAgreementId: this.workerAgreement!.Id,
    EmploymentTypeId: this.workerAgreement!.EmploymentTypeId,
    HasNextYearTimeLogging: this.workerAgreement!.HasNextYearTimeLogging,
    // Null while loading (e.g. opened from URL) - the form then fetches the periods itself.
    WorkingTimePeriods: this.workingTimePeriodsLoading() ? null : this.workingTimePeriods(),
    WorkingTimePeriod: !absence && this.isScaled() ? this.selectedPeriod() : null,
  });

  private loadWorkingTimePeriods(agreement: AgreementToPick | null, force = false): void {
    const agreementId = agreement?.Id ?? null;
    if (!force && agreementId === this.workingTimePeriodsAgreementId) {
      return;
    }

    const sameAgreement = agreementId === this.workingTimePeriodsAgreementId;
    this.workingTimePeriodsAgreementId = agreementId;
    this.workingTimePeriodsSubscription?.unsubscribe();
    this.workingTimePeriodsError.set(false);
    this.workingTimePeriodsLoading.set(agreementId != null);
    if (!sameAgreement) {
      this.workingTimePeriods.set(null);
      this.selectedPeriod.set(null);
    }

    if (agreementId == null) {
      return;
    }

    this.workingTimePeriodsSubscription = this.absenceService
      .getWorkingTimePeriods(agreementId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (periods) => {
          const current = sameAgreement ? this.selectedPeriod() : null;
          const matching = current ? periods.Periods.find((period) => toDateKey(period.StartDate) === toDateKey(current.StartDate))
            ?? findPeriod(periods.Periods, current.StartDate) : null;
          this.selectedPeriod.set(matching ?? null);
          this.workingTimePeriods.set(periods);
          this.workingTimePeriodsLoading.set(false);
        },
        error: () => {
          this.workingTimePeriods.set(null);
          this.selectedPeriod.set(null);
          this.workingTimePeriodsLoading.set(false);
          this.workingTimePeriodsError.set(true);
          this.workingTimePeriodsAgreementId = null;
        },
      });
  }

  private setSelectedTabFromUrl(): void {
    const availableNavigation = this.navigation.filter(n => n.guardPredicate());
    if (!availableNavigation.length) {
      this.selectedNavigation = undefined;
      return;
    }

    let index = availableNavigation.findIndex(n => n.listType === this.localTabType);
    if (index < 0) {
      index = 0;
    }

    this.selectedNavigation = availableNavigation[index];
    const tabGroup = this.tabGroup();
    if (!tabGroup) {
      return;
    }

    tabGroup.selectedIndex = index;
  }

  onTabChanged(event: MatTabChangeEvent): void {
    const availableNavigation = this.navigation.filter(n => n.guardPredicate());
    if (!availableNavigation.length || event.index < 0 || event.index >= availableNavigation.length) {
      return;
    }

    this.selectedNavigation = availableNavigation[event.index];

    this.setUrl();
  }

  async refreshAbsences(workerAgreement: AgreementToPick): Promise<void> {
    if (this.isRefreshRequestInProgress()) {
      return;
    }

    this.isRefreshRequestInProgress.set(true);

    try {
      await firstValueFrom(this.absenceService.refreshAbsences(workerAgreement.Id));
      this.snackbar.openSuccessSnackBar(Messages.SuccessfullySendAbsencesRefreshRequest);
      this.triggerReload();
    } finally {
      this.isRefreshRequestInProgress.set(false);
    }
  }

  private openModal(absenceId: number) {
    const absence = this.absenceService.getAbsence(absenceId);

    combineLatest([this.agreements$, absence])
      .pipe(
        filter(([agreements, absenceData]) => !!absenceData && agreements.some((agreement) => agreement.Id === absenceData.WorkerAgreementId)),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(async ([agreements, absenceData]) => {
        if (!absenceData) {
          return;
        }

        this.workerAgreement = agreements.find((agreement: AgreementToPick) => agreement.Id === absenceData.WorkerAgreementId) ?? null;
        this.loadWorkingTimePeriods(this.workerAgreement);

        if (this.workerAgreement) {
          await this.openAbsenceModal(absenceData);
          this.setSelectedTabFromUrl();
          this.setUrl(null);
        }
      });
  }

  private setUrl(absenceId: string | null = null) {
    if (this.selectedNavigation) {
      this.localTabType = this.selectedNavigation.listType;
      const command = [this.selectedNavigation.link];

      if (this.selectedNavigation.listType === AbsenceTabsType.Absences) {
        command.push(this.selectedYear.toString());
      }

      if (absenceId) {
        command.push(absenceId);
      }

      const url = this.router.createUrlTree(command, { relativeTo: this.route.parent }).toString()
      this.location.go(url);
    }
  }

  private tryParseYear(value: string | undefined): number | null {
    if (!value) {
      return null;
    }

    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : null;
  }
}
